import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import paymentAPI from '../services/paymentAPI';

const PRICE_UNLIMITED_MONTHLY = process.env.REACT_APP_STRIPE_PRICE_UNLIMITED_MONTHLY || '';
const PRICE_3_CREDITS = process.env.REACT_APP_STRIPE_PRICE_3_CREDITS || '';
const PRICE_10_CREDITS = process.env.REACT_APP_STRIPE_PRICE_10_CREDITS || '';

const plans = [
  {
    id: 'credits_3',
    priceId: PRICE_3_CREDITS,
    name: '3 Resume Pack',
    price: '$5',
    period: 'one-time',
    credits: '3 Optimizations',
    description: 'Perfect for targeting a few key roles with laser-focused resumes.',
    features: ['3 AI resume optimizations', 'Full DOCX download', 'ATS keyword matching', 'Skill gap analysis'],
    cta: 'Buy 3 Credits',
    accent: 'var(--color-charcoal-800)',
    badge: null,
    isPopular: false,
  },
  {
    id: 'unlimited_monthly',
    priceId: PRICE_UNLIMITED_MONTHLY,
    name: 'Unlimited Monthly',
    price: '$20',
    period: '/ month',
    credits: 'Unlimited',
    description: 'Dominate your job search with unlimited AI-powered resume optimization.',
    features: ['Unlimited optimizations', 'Full DOCX download', 'ATS keyword matching', 'Skill gap analysis', 'Priority processing', 'Application history tracker'],
    cta: 'Start Unlimited',
    accent: 'var(--color-accent-ferrari)',
    badge: 'Most Popular',
    isPopular: true,
  },
  {
    id: 'credits_10',
    priceId: PRICE_10_CREDITS,
    name: '10 Resume Pack',
    price: '$15',
    period: 'one-time',
    credits: '10 Optimizations',
    description: 'Maximize your reach across many applications with a bulk credit pack.',
    features: ['10 AI resume optimizations', 'Full DOCX download', 'ATS keyword matching', 'Skill gap analysis', 'Application history tracker'],
    cta: 'Buy 10 Credits',
    accent: 'var(--valley-emerald)',
    badge: 'Best Value',
    isPopular: false,
  },
];

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.12" />
    <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      paymentAPI.getStatus()
        .then(r => setPaymentStatus(r.data))
        .catch(() => {});
    }
  }, [user]);

  const handleSelectPlan = async (plan) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setLoadingPlanId(plan.id);
    try {
      await paymentAPI.redirectToCheckout(plan.priceId);
    } catch (err) {
      alert(err.message || 'Something went wrong. Please try again.');
      setLoadingPlanId(null);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      await paymentAPI.cancelSubscription();
      const r = await paymentAPI.getStatus();
      setPaymentStatus(r.data);
      setShowCancelConfirm(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel subscription.');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsReactivating(true);
    try {
      await paymentAPI.reactivateSubscription();
      const r = await paymentAPI.getStatus();
      setPaymentStatus(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reactivate subscription.');
    } finally {
      setIsReactivating(false);
    }
  };

  const isSubscribed = paymentStatus?.is_subscribed;
  const cancelAtPeriodEnd = paymentStatus?.cancel_at_period_end;
  const currentPeriodEnd = paymentStatus?.current_period_end;
  const credits = paymentStatus?.credits ?? null;

  return (
    <div style={styles.page}>
      {/* Background */}
      <div className="auth-blob-decoration" style={styles.bgOrb1} aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.bgOrb2} aria-hidden="true" />

      {/* Header */}
      <header className="pricing-header-responsive" style={styles.header}>
        <button onClick={() => navigate(user ? '/dashboard' : '/')} style={styles.backBtn}>
          <span style={{ fontSize: 18 }}>←</span>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 20 }}>RezFlow</span>
        </button>
      </header>

      <main className="pricing-main-responsive" style={styles.main}>
        {/* Hero */}
        <div style={styles.heroSection}>
          <div style={styles.badge}>✦ Simple, transparent pricing</div>
          <h1 style={styles.headline}>
            Land your dream job<br />
            <span style={styles.headlineAccent}>without breaking the bank.</span>
          </h1>
          <p style={styles.subheadline}>
            Every new account starts with <strong>1 free optimization</strong>. No card required.
          </p>

          {/* Current status pill */}
          {user && paymentStatus && (
            <div style={styles.statusPill}>
              {isSubscribed ? (
                cancelAtPeriodEnd ? (
                  <>⚠ <strong>Subscription canceling,</strong> access until {currentPeriodEnd
                    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'billing period ends'}</>
                ) : (
                  <>✅ <strong>Pro Subscriber</strong> — Unlimited optimizations active</>
                )
              ) : (
                <>⬡ <strong>{credits ?? 0} credit{credits !== 1 ? 's' : ''}</strong> remaining</>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="pricing-grid-responsive" style={styles.cardsGrid} role="list">
          {plans.map((plan) => (
            <article
              key={plan.id}
              style={{
                ...styles.card,
                ...(plan.isPopular ? styles.cardPopular : {}),
              }}
              role="listitem"
            >
              {/* Badge */}
              {plan.badge && (
                <div style={{
                  ...styles.cardBadge,
                  background: plan.isPopular ? 'var(--color-accent-ferrari)' : 'var(--valley-emerald)',
                }}>
                  {plan.badge}
                </div>
              )}

              {/* Plan header */}
              <div style={styles.cardHeader}>
                <p style={{ ...styles.planName, color: plan.accent }}>{plan.name}</p>
                <div style={styles.priceRow}>
                  <span style={styles.priceAmount}>{plan.price}</span>
                  <span style={styles.pricePeriod}>{plan.period}</span>
                </div>
                <p style={styles.creditsLabel}>{plan.credits}</p>
                <p style={styles.planDesc}>{plan.description}</p>
              </div>

              {/* Features */}
              <ul style={styles.featureList} aria-label={`${plan.name} features`}>
                {plan.features.map((f) => (
                  <li key={f} style={styles.featureItem}>
                    <span style={{ color: plan.accent, flexShrink: 0 }}>
                      <CheckIcon />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                id={`pricing-cta-${plan.id}`}
                onClick={() => handleSelectPlan(plan)}
                disabled={loadingPlanId === plan.id || (isSubscribed && plan.id === 'unlimited_monthly')}
                style={{
                  ...styles.ctaBtn,
                  background: plan.isPopular
                    ? 'linear-gradient(135deg, var(--color-accent-ferrari), var(--color-purple-600))'
                    : 'transparent',
                  color: plan.isPopular ? '#fff' : plan.accent,
                  border: plan.isPopular ? 'none' : `2px solid ${plan.accent}`,
                  opacity: (isSubscribed && plan.id === 'unlimited_monthly') ? 0.5 : 1,
                }}
              >
                {loadingPlanId === plan.id ? (
                  <span style={styles.spinner} />
                ) : isSubscribed && plan.id === 'unlimited_monthly' ? (
                  cancelAtPeriodEnd ? 'Renew Subscription' : '✓ Current Plan'
                ) : (
                  plan.cta
                )}
              </button>

              {/* Subscription management for the unlimited plan card */}
              {isSubscribed && plan.id === 'unlimited_monthly' && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  {cancelAtPeriodEnd ? (
                    <button
                      id="pricing-reactivate-btn"
                      onClick={handleReactivateSubscription}
                      disabled={isReactivating}
                      style={{
                        background: 'linear-gradient(135deg, var(--color-accent-ferrari), var(--color-purple-600))',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        padding: '10px 20px',
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: isReactivating ? 'not-allowed' : 'pointer',
                        opacity: isReactivating ? 0.7 : 1,
                        transition: 'all var(--transition-fast)',
                        width: '100%',
                      }}
                    >
                      {isReactivating ? 'Reactivating...' : 'Keep My Subscription'}
                    </button>
                  ) : !showCancelConfirm ? (
                    <button
                      id="pricing-show-cancel-btn"
                      onClick={() => setShowCancelConfirm(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text-muted)',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '4px 0',
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      Cancel subscription
                    </button>
                  ) : (
                    <div style={{
                      background: 'var(--color-error-bg)',
                      border: '1px solid var(--color-error-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '14px 16px',
                      textAlign: 'left',
                      animation: 'rz-fadeIn 0.2s ease',
                    }}>
                      <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0 0 4px 0' }}>
                        Cancel your subscription?
                      </p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                        You'll keep unlimited access until the end of your current billing period.
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          id="pricing-confirm-cancel-btn"
                          onClick={handleCancelSubscription}
                          disabled={isCanceling}
                          style={{
                            flex: 1,
                            background: 'var(--color-error)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            padding: '9px 16px',
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            cursor: isCanceling ? 'not-allowed' : 'pointer',
                            opacity: isCanceling ? 0.7 : 1,
                          }}
                        >
                          {isCanceling ? 'Canceling...' : 'Yes, Cancel'}
                        </button>
                        <button
                          id="pricing-dismiss-cancel-btn"
                          onClick={() => setShowCancelConfirm(false)}
                          style={{
                            flex: 1,
                            background: 'var(--color-surface)',
                            color: 'var(--color-text-secondary)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '9px 16px',
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                          }}
                        >
                          Never mind
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Free tier note */}
        <p style={styles.freeNote}>
          🎉 New users start with <strong>1 free optimization,</strong> no credit card required.
          Upgrade only when you need more.
        </p>

        {/* Stripe badge */}
        <div style={styles.stripeBadge}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 1L10.163 5.284L15 6.009L11.5 9.406L12.326 14.2L8 11.934L3.674 14.2L4.5 9.406L1 6.009L5.837 5.284L8 1Z" fill="#6366f1" />
          </svg>
          Payments secured by Stripe. We never store your card details.
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #f8fafc 0%, #eef2ff 50%, #faf5ff 100%)',
    fontFamily: 'var(--font-body)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgOrb1: {
    position: 'fixed',
    top: '-120px',
    right: '-120px',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  bgOrb2: {
    position: 'fixed',
    bottom: '-100px',
    left: '-100px',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    padding: '18px 32px',
    background: 'rgba(255,255,255,0.75)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderBottom: '1px solid rgba(226,232,240,0.6)',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-primary)',
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    transition: 'background var(--transition-fast)',
  },
  main: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '60px 24px 80px',
    position: 'relative',
    zIndex: 1,
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: 60,
    animation: 'rz-fadeIn 0.5s cubic-bezier(0.16,1,0.3,1)',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    background: 'rgba(79,70,229,0.08)',
    color: 'var(--color-accent-ferrari)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    marginBottom: 20,
    border: '1px solid rgba(79,70,229,0.15)',
  },
  headline: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'clamp(2rem, 4vw, 3.2rem)',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.04em',
    color: 'var(--color-text-primary)',
    marginBottom: 16,
  },
  headlineAccent: {
    background: 'linear-gradient(135deg, var(--color-accent-ferrari), var(--color-purple-600))',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subheadline: {
    fontSize: '1.1rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
    marginBottom: 24,
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(226,232,240,0.8)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.9rem',
    color: 'var(--color-text-secondary)',
    boxShadow: 'var(--shadow-sm)',
    animation: 'rz-fadeIn 0.4s ease',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 24,
    alignItems: 'stretch',
    marginBottom: 40,
  },
  card: {
    background: 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(226,232,240,0.8)',
    borderRadius: 'var(--radius-xl)',
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform var(--transition-base), box-shadow var(--transition-base)',
    boxShadow: 'var(--shadow-md)',
    animation: 'rz-fadeIn 0.5s cubic-bezier(0.16,1,0.3,1)',
  },
  cardPopular: {
    border: '2px solid rgba(79,70,229,0.4)',
    boxShadow: 'var(--shadow-lg), 0 0 40px rgba(79,70,229,0.1)',
    transform: 'translateY(-4px)',
  },
  cardBadge: {
    position: 'absolute',
    top: 20,
    right: 20,
    padding: '4px 12px',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  cardHeader: {
    marginBottom: 24,
  },
  planName: {
    fontFamily: 'var(--font-heading)',
    fontWeight: 700,
    fontSize: '0.85rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 4,
  },
  priceAmount: {
    fontFamily: 'var(--font-heading)',
    fontSize: '2.8rem',
    fontWeight: 800,
    letterSpacing: '-0.04em',
    color: 'var(--color-text-primary)',
    lineHeight: 1,
  },
  pricePeriod: {
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    fontWeight: 500,
  },
  creditsLabel: {
    fontFamily: 'var(--font-heading)',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: 'var(--color-text-secondary)',
    marginBottom: 10,
  },
  planDesc: {
    fontSize: '0.88rem',
    color: 'var(--color-text-muted)',
    lineHeight: 1.6,
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: 1,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: '0.9rem',
    color: 'var(--color-text-secondary)',
  },
  ctaBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    letterSpacing: '-0.01em',
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid currentColor',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'rz-spin 0.7s linear infinite',
  },
  freeNote: {
    textAlign: 'center',
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    marginBottom: 20,
    lineHeight: 1.7,
  },
  stripeBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
  },
};
